import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3,
  Building2,
  CalendarRange,
  ChevronDown,
  ClipboardCheck,
  MapPinned,
  RefreshCw,
  ScanLine,
  Search,
  TriangleAlert,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { BarRow, fmtDateFr, fmtInt, fmtPct, Kpi } from './statisticsShared';
import { AdminPageHeader, adminPageStackClass } from './AdminChrome';

const StatistiquesPage = ({ token, apiBase, theme, onUnauthorized }) => {
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [enterprisePopoverOpen, setEnterprisePopoverOpen] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState('');

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleApiError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      const msg =
        error.response?.data?.detail || error.message || 'Impossible de charger les données';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    },
    [onUnauthorized]
  );

  const loadSites = useCallback(async () => {
    setSitesLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/sites`, { headers: authHeaders });
      const list = Array.isArray(data) ? data : [];
      setSites(
        list
          .map((s) => ({
            id: String(s.id ?? s._id ?? ''),
            nom: (s.nom || s.name || '').trim() || '—',
          }))
          .filter((s) => s.id)
      );
    } catch (e) {
      handleApiError(e);
      setSites([]);
    } finally {
      setSitesLoading(false);
    }
  }, [apiBase, authHeaders, handleApiError]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (selectedIds.size > 0) {
        params.set('entreprise_ids', [...selectedIds].join(','));
      }
      const q = params.toString();
      const url = `${apiBase}/admin/compte-rendu/stats${q ? `?${q}` : ''}`;
      const { data } = await axios.get(url, { headers: authHeaders });
      setStats(data);
    } catch (e) {
      handleApiError(e);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [apiBase, authHeaders, dateFrom, dateTo, selectedIds, handleApiError]);

  useEffect(() => {
    if (!token) return;
    loadStats();
  }, [token, loadStats]);

  const toggleSite = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllSites = () => {
    setSelectedIds(new Set(sites.map((s) => s.id)));
  };

  const clearSites = () => setSelectedIds(new Set());

  const period = stats?.period;
  const visibility = stats?.visibility;
  const showScansSection =
    !!stats && (visibility != null ? visibility.scans !== false : stats.scans != null);
  const showQualitySection =
    !!stats && (visibility != null ? visibility.quality !== false : stats.quality != null);
  const scans = showScansSection && stats?.scans ? stats.scans : null;
  const quality = showQualitySection && stats?.quality ? stats.quality : null;
  const alerts = stats?.alerts;

  const kpiSlots = (showScansSection ? 2 : 0) + (showQualitySection ? 2 : 0);
  const kpiGridClass =
    kpiSlots <= 1
      ? 'grid gap-4 grid-cols-1'
      : kpiSlots === 2
        ? 'grid gap-4 sm:grid-cols-2'
        : kpiSlots === 3
          ? 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3'
          : 'grid gap-4 sm:grid-cols-2 xl:grid-cols-4';

  const maxDay = useMemo(() => {
    const rows = scans?.by_day || [];
    return Math.max(1, ...rows.map((r) => r.count || 0));
  }, [scans]);

  const maxSite = useMemo(() => {
    const rows = scans?.by_site || [];
    return Math.max(1, ...rows.map((r) => r.count || 0));
  }, [scans]);

  const gpsTotal =
    (scans?.gps?.precision_ok_le_1km || 0) +
    (scans?.gps?.precision_weak_gt_1km || 0) +
    (scans?.gps?.precision_unknown || 0);

  const scoreDist = quality?.score_distribution || {};
  const scoreKeys = ['0', '1', '2', '3'];
  const scoreTotal = scoreKeys.reduce((acc, k) => acc + (Number(scoreDist[k]) || 0), 0);
  const scoreMax = Math.max(1, ...scoreKeys.map((k) => Number(scoreDist[k]) || 0));

  const filteredSites = useMemo(() => {
    const q = enterpriseSearch.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter((s) => s.nom.toLowerCase().includes(q));
  }, [sites, enterpriseSearch]);

  const periodSummary = useMemo(() => {
    if (!period) return null;
    const a = fmtDateFr(period.date_from);
    const b = fmtDateFr(period.date_to);
    const labels = period.entreprises_selected || [];
    const active = period.entreprise_filter_active;
    let scope = 'Toutes les entreprises';
    if (active && labels.length > 0) {
      scope = `${labels.length} entreprise(s)`;
    } else if (active) {
      scope = 'Filtre entreprise (aucune reconnue)';
    }
    return `${a} → ${b} · ${scope}`;
  }, [period]);

  return (
    <div className={adminPageStackClass}>
      <AdminPageHeader
        theme={theme}
        icon={BarChart3}
        title="Statistiques"
        subtitle={
          <>
            Tableau de bord interne (filtres multi-entreprises). Pour générer un PDF ou Excel à destination
            d’un client, utilisez la page <strong>Compte rendu</strong>.
          </>
        }
        footer={
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
            {periodSummary ? (
              <p
                className="text-xs font-medium truncate min-w-0 max-w-full lg:max-w-[50%]"
                style={{ color: theme.textSecondary }}
                title={periodSummary}
              >
                {periodSummary}
              </p>
            ) : (
              <span className="hidden lg:block" />
            )}

          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-2.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              <CalendarRange className="h-3.5 w-3.5 shrink-0 hidden sm:block" style={{ color: theme.accent }} />
              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor="st-df"
                  className="text-[11px] sr-only sm:not-sr-only sm:w-5 shrink-0"
                  style={{ color: theme.textSecondary }}
                >
                  Du
                </Label>
                <Input
                  id="st-df"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 w-[10.5rem] sm:w-[11rem] text-sm rounded-md"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.surface }}
                />
              </div>
              <span className="text-xs shrink-0" style={{ color: theme.textSecondary }}>
                au
              </span>
              <div className="flex items-center gap-1.5">
                <Label htmlFor="st-dt" className="text-[11px] sr-only" style={{ color: theme.textSecondary }}>
                  Au
                </Label>
                <Input
                  id="st-dt"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 w-[10.5rem] sm:w-[11rem] text-sm rounded-md"
                  style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.surface }}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Popover
                open={enterprisePopoverOpen}
                onOpenChange={(open) => {
                  setEnterprisePopoverOpen(open);
                  if (!open) setEnterpriseSearch('');
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1.5 rounded-md justify-between min-w-[9.5rem] sm:min-w-[10.5rem]"
                    style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                  >
                    <span className="flex items-center gap-1.5 min-w-0">
                      <Building2 className="h-3.5 w-3.5 shrink-0 opacity-80" />
                      <span className="truncate text-sm font-medium">Entreprises</span>
                      {selectedIds.size > 0 ? (
                        <Badge
                          variant="secondary"
                          className="h-5 min-w-[1.25rem] px-1.5 text-[10px] font-semibold tabular-nums shrink-0"
                          style={{ backgroundColor: `${theme.accent}22`, color: theme.accent }}
                        >
                          {selectedIds.size}
                        </Badge>
                      ) : null}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[min(100vw-2rem,20rem)] p-0 overflow-hidden"
                  align="end"
                  sideOffset={6}
                  style={{ borderColor: theme.border, backgroundColor: theme.surface }}
                >
                  <div className="p-3 border-b space-y-2" style={{ borderColor: theme.border }}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold" style={{ color: theme.text }}>
                        Filtrer par entreprise
                      </p>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={selectAllSites}
                          disabled={sitesLoading || sites.length === 0}
                        >
                          Tout
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={clearSites}
                          disabled={selectedIds.size === 0}
                        >
                          Aucun
                        </Button>
                      </div>
                    </div>
                    <div className="relative">
                      <Search
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 opacity-45"
                        style={{ color: theme.textSecondary }}
                      />
                      <Input
                        type="search"
                        placeholder="Rechercher…"
                        value={enterpriseSearch}
                        onChange={(e) => setEnterpriseSearch(e.target.value)}
                        className="h-8 pl-8 text-sm rounded-md"
                        style={{ borderColor: theme.border, color: theme.text, backgroundColor: theme.background }}
                      />
                    </div>
                  </div>
                  <ScrollArea className="h-[min(14rem,40vh)]">
                    <div className="p-2 space-y-0.5">
                      {sitesLoading ? (
                        <p className="text-xs px-2 py-4 text-center" style={{ color: theme.textSecondary }}>
                          Chargement…
                        </p>
                      ) : sites.length === 0 ? (
                        <p className="text-xs px-2 py-4 text-center" style={{ color: theme.textSecondary }}>
                          Aucun site.
                        </p>
                      ) : filteredSites.length === 0 ? (
                        <p className="text-xs px-2 py-4 text-center" style={{ color: theme.textSecondary }}>
                          Aucun résultat.
                        </p>
                      ) : (
                        filteredSites.map((s) => (
                          <label
                            key={s.id}
                            className="flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
                            style={{ color: theme.text }}
                          >
                            <Checkbox
                              checked={selectedIds.has(s.id)}
                              onCheckedChange={() => toggleSite(s.id)}
                            />
                            <span className="text-sm truncate" title={s.nom}>
                              {s.nom}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                  <p
                    className="text-[10px] px-3 py-2 leading-relaxed border-t"
                    style={{ borderColor: theme.border, color: theme.textSecondary }}
                  >
                    Vide = vue globale. Filtre les scans (site) et les contrôles qualité (id entreprise).
                  </p>
                </PopoverContent>
              </Popover>

              <Button
                type="button"
                onClick={loadStats}
                disabled={statsLoading}
                size="sm"
                className="h-9 rounded-md gap-1.5 shrink-0"
                style={{ backgroundColor: theme.accent, color: '#fff' }}
              >
                {statsLoading ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    <RefreshCw className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Actualiser</span>
                  </>
                )}
              </Button>
            </div>
          </div>
          </div>
        }
      />

      {alerts &&
        ((showScansSection && (alerts.entreprises_no_scans_in_filter || []).length > 0) ||
          (showQualitySection && (alerts.entreprises_no_validated_cq_in_filter || []).length > 0)) && (
          <Alert
            variant="default"
            className="py-2.5 px-3 border-amber-300/80 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
          >
            <TriangleAlert className="h-4 w-4" />
            <AlertTitle>Points d’attention</AlertTitle>
            <AlertDescription className="space-y-2 mt-1">
              {showScansSection && (alerts.entreprises_no_scans_in_filter || []).length > 0 && (
                <p className="text-sm">
                  <strong>Aucun scan</strong> sur la période pour :{' '}
                  {(alerts.entreprises_no_scans_in_filter || []).join(', ')}.
                </p>
              )}
              {showQualitySection && (alerts.entreprises_no_validated_cq_in_filter || []).length > 0 && (
                <p className="text-sm">
                  <strong>Aucun contrôle qualité validé</strong> sur la période pour :{' '}
                  {(alerts.entreprises_no_validated_cq_in_filter || []).join(', ')}.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

      {statsLoading && !stats ? (
        <div
          className="rounded-2xl border p-12 text-center text-sm"
          style={{ borderColor: theme.border, color: theme.textSecondary }}
        >
          Chargement des statistiques…
        </div>
      ) : stats ? (
        <>
          {(!showScansSection || !showQualitySection) && (
            <Alert
              variant="default"
              className="py-2.5 px-3 border-sky-200/90 bg-sky-50 text-sky-950 dark:border-sky-800 dark:bg-sky-950/35 dark:text-sky-100"
            >
              <AlertTitle className="text-sm font-semibold">Périmètre des indicateurs</AlertTitle>
              <AlertDescription className="text-sm">
                {!showScansSection && !showQualitySection
                  ? 'Vous avez accès aux Statistiques mais ni aux suivis (scans) ni au module visite terrain : aucun indicateur agrégé ne peut être affiché. Demandez les permissions « Accéder à la page Suivis » et/ou « Accéder à Visite terrain ».'
                  : !showScansSection
                    ? 'Les indicateurs et graphiques liés aux scans sont masqués : permission « Accéder à la page Suivis » requise.'
                    : 'Les indicateurs et graphiques liés au contrôle qualité sont masqués : permission « Accéder à Visite terrain » (ou une sous-permission visite) requise.'}
              </AlertDescription>
            </Alert>
          )}

          {kpiSlots > 0 ? (
            <div className={kpiGridClass}>
              {showScansSection ? (
                <>
                  <Kpi
                    theme={theme}
                    title="Scans (période)"
                    value={fmtInt(scans?.total)}
                    subtitle={`${fmtInt(scans?.unique_employees)} salarié(s) distinct(s)`}
                  />
                  <Kpi
                    theme={theme}
                    title="Sites avec scans"
                    value={fmtInt(scans?.active_sites_count)}
                    subtitle="Sites distincts sur la période"
                  />
                </>
              ) : null}
              {showQualitySection ? (
                <>
                  <Kpi
                    theme={theme}
                    title="Contrôles qualité"
                    value={fmtInt(quality?.total_controls)}
                    subtitle={`${fmtInt(quality?.validated_count)} validé(s) sur ${fmtInt(quality?.total_controls)} · taux ${fmtPct(quality?.validation_rate_pct)}`}
                  />
                  <Kpi
                    theme={theme}
                    title="Note moyenne (validés)"
                    value={
                      quality?.avg_score_validated_only != null
                        ? Number(quality.avg_score_validated_only).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : '—'
                    }
                    subtitle="Échelle 0 à 3"
                  />
                </>
              ) : null}
            </div>
          ) : null}

          <div
            className={`grid gap-6 ${showScansSection && showQualitySection ? 'xl:grid-cols-2' : 'grid-cols-1'}`}
          >
            {showScansSection ? (
            <Card className="rounded-2xl border shadow-sm" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
              <CardHeader className="p-4 pb-2 pt-3.5 space-y-0">
                <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                  <ScanLine className="h-4 w-4" style={{ color: theme.accent }} />
                  Scans terrain
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0 space-y-5">
                <div>
                  <p className="text-xs font-medium mb-2" style={{ color: theme.textSecondary }}>
                    Répartition arrivée / départ
                  </p>
                  <div className="space-y-2">
                    <BarRow
                      label="Arrivée"
                      value={scans?.by_type?.Arrivee ?? 0}
                      max={Math.max(1, scans?.total || 0)}
                      color={theme.accent}
                      mutedColor={theme.textSecondary}
                    />
                    <BarRow
                      label="Départ"
                      value={scans?.by_type?.Depart ?? 0}
                      max={Math.max(1, scans?.total || 0)}
                      color="#0891b2"
                      mutedColor={theme.textSecondary}
                    />
                    <BarRow
                      label="Autre"
                      value={scans?.by_type?.Autre ?? 0}
                      max={Math.max(1, scans?.total || 0)}
                      color="#94a3b8"
                      mutedColor={theme.textSecondary}
                    />
                  </div>
                </div>
                <Separator style={{ backgroundColor: theme.border }} />
                <div>
                  <p className="text-xs font-medium mb-2 flex items-center gap-1" style={{ color: theme.textSecondary }}>
                    <MapPinned className="h-3.5 w-3.5" />
                    Précision GPS
                  </p>
                  <div className="space-y-2">
                    <BarRow
                      label="≤ 1 km"
                      value={scans?.gps?.precision_ok_le_1km ?? 0}
                      max={Math.max(1, gpsTotal)}
                      color="#16a34a"
                      mutedColor={theme.textSecondary}
                    />
                    <BarRow
                      label="> 1 km"
                      value={scans?.gps?.precision_weak_gt_1km ?? 0}
                      max={Math.max(1, gpsTotal)}
                      color="#ca8a04"
                      mutedColor={theme.textSecondary}
                    />
                    <BarRow
                      label="Inconnue"
                      value={scans?.gps?.precision_unknown ?? 0}
                      max={Math.max(1, gpsTotal)}
                      color="#64748b"
                      mutedColor={theme.textSecondary}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            ) : null}
            {showQualitySection ? (
            <Card className="rounded-2xl border shadow-sm" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
              <CardHeader className="p-4 pb-2 pt-3.5 space-y-0">
                <CardTitle className="text-sm sm:text-base font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                  <ClipboardCheck className="h-4 w-4" style={{ color: theme.accent }} />
                  Contrôle qualité
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0 space-y-5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div
                    className="rounded-lg border p-3"
                    style={{ borderColor: theme.border, backgroundColor: theme.background }}
                  >
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      Brouillons
                    </p>
                    <p className="text-lg font-semibold tabular-nums" style={{ color: theme.text }}>
                      {fmtInt(quality?.draft_count)}
                    </p>
                  </div>
                  <div
                    className="rounded-lg border p-3"
                    style={{ borderColor: theme.border, backgroundColor: theme.background }}
                  >
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      Commentaires (validés)
                    </p>
                    <p className="text-xs mt-1" style={{ color: theme.text }}>
                      Thème : {fmtPct(quality?.comments?.validated_with_theme_comment_pct)}
                    </p>
                    <p className="text-xs" style={{ color: theme.text }}>
                      Critère : {fmtPct(quality?.comments?.validated_with_criterion_comment_pct)}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium mb-2" style={{ color: theme.textSecondary }}>
                    Répartition des notes (validés, échantillon : {fmtInt(scoreTotal)})
                  </p>
                  <div className="space-y-2">
                    {scoreKeys.map((k) => (
                      <BarRow
                        key={k}
                        label={`Note ${k}`}
                        value={Number(scoreDist[k]) || 0}
                        max={scoreMax}
                        color={theme.accent}
                        mutedColor={theme.textSecondary}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
            ) : null}
          </div>

          {showScansSection ? (
          <div className="grid gap-6 xl:grid-cols-2">
            <Card className="rounded-2xl border shadow-sm" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
              <CardHeader className="p-4 pb-2 pt-3.5 space-y-0">
                <CardTitle className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>
                  Scans par jour
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {(scans?.by_day || []).length === 0 ? (
                    <p className="text-sm" style={{ color: theme.textSecondary }}>
                      Aucun scan sur la période.
                    </p>
                  ) : (
                    (scans.by_day || []).map((row) => (
                      <BarRow
                        key={row.date}
                        label={fmtDateFr(row.date)}
                        value={row.count}
                        max={maxDay}
                        color={theme.accent}
                        mutedColor={theme.textSecondary}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border shadow-sm" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
              <CardHeader className="p-4 pb-2 pt-3.5 space-y-0">
                <CardTitle className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>
                  Top sites (scans)
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {(scans?.by_site || []).length === 0 ? (
                    <p className="text-sm" style={{ color: theme.textSecondary }}>
                      Aucune donnée agrégée par site.
                    </p>
                  ) : (
                    (scans.by_site || []).map((row) => (
                      <BarRow
                        key={row.site}
                        label={row.site || '—'}
                        value={row.count}
                        max={maxSite}
                        color="#0e7490"
                        mutedColor={theme.textSecondary}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          ) : null}

          {showQualitySection ? (
          <div className="grid gap-6 xl:grid-cols-2">
            <Card className="rounded-2xl border shadow-sm" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
              <CardHeader className="p-4 pb-2 pt-3.5 space-y-0">
                <CardTitle className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>
                  Moyennes par formulaire (validés)
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ borderColor: theme.border }}>
                      <TableHead style={{ color: theme.text }}>Formulaire</TableHead>
                      <TableHead className="text-right" style={{ color: theme.text }}>
                        Contrôles
                      </TableHead>
                      <TableHead className="text-right" style={{ color: theme.text }}>
                        Moyenne
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(quality?.by_form || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-sm" style={{ color: theme.textSecondary }}>
                          Aucun contrôle validé avec scores.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (quality.by_form || []).map((f) => (
                        <TableRow key={f.form_id} style={{ borderColor: theme.border }}>
                          <TableCell className="font-medium max-w-[220px] truncate" title={f.form_name}>
                            {f.form_name || '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{fmtInt(f.validated_count)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {f.avg_score != null
                              ? Number(f.avg_score).toLocaleString('fr-FR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border shadow-sm" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
              <CardHeader className="p-4 pb-2 pt-3.5 space-y-0">
                <CardTitle className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>
                  Critères les plus faibles (validés)
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ borderColor: theme.border }}>
                      <TableHead style={{ color: theme.text }}>Critère</TableHead>
                      <TableHead style={{ color: theme.text }}>Thème</TableHead>
                      <TableHead className="text-right" style={{ color: theme.text }}>
                        Moy.
                      </TableHead>
                      <TableHead className="text-right" style={{ color: theme.text }}>
                        n
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(quality?.weakest_criteria || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-sm" style={{ color: theme.textSecondary }}>
                          Pas assez de données pour classer les critères.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (quality.weakest_criteria || []).map((w) => (
                        <TableRow key={w.criterion_id} style={{ borderColor: theme.border }}>
                          <TableCell
                            className="font-medium max-w-[200px]"
                            title={`${w.form_name} · ${w.criterion_name}`}
                          >
                            <span className="line-clamp-2">{w.criterion_name}</span>
                          </TableCell>
                          <TableCell className="max-w-[140px] truncate text-sm" style={{ color: theme.textSecondary }}>
                            {w.theme_name}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {w.avg_score != null
                              ? Number(w.avg_score).toLocaleString('fr-FR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                              : '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{fmtInt(w.sample_count)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
          ) : null}
        </>
      ) : (
        <div
          className="rounded-2xl border p-8 text-center text-sm"
          style={{ borderColor: theme.border, color: theme.textSecondary }}
        >
          Impossible d’afficher les statistiques. Vérifiez la connexion ou réessayez.
        </div>
      )}
    </div>
  );
};

export default StatistiquesPage;
